import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  CircleAlert,
  Cloud,
  Database,
  Eye,
  EyeOff,
  KeyRound,
  Link2,
  LockKeyhole,
  Radio,
  Save,
  Send,
  ShieldAlert
} from "lucide-react";
import { client, RequestError } from "./api";

type IntegrationKind = "OIDC" | "TELEGRAM_SOURCE" | "FCM";

type IntegrationTestResult = { ok: boolean; message?: string; identity?: string };

type FormState = {
  publicUrl: string;
  webUrl: string;
  oidcIssuer: string;
  oidcClientId: string;
  oidcClientSecret: string;
  telegramApiId: string;
  telegramApiHash: string;
  telegramSession: string;
  telegramAdminIds: string;
  fcmServiceAccountJson: string;
};

const emptyForm: FormState = {
  publicUrl: "",
  webUrl: "",
  oidcIssuer: "",
  oidcClientId: "",
  oidcClientSecret: "",
  telegramApiId: "",
  telegramApiHash: "",
  telegramSession: "",
  telegramAdminIds: "",
  fcmServiceAccountJson: ""
};

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["settings"], queryFn: () => client.settings() });
  const [form, setForm] = useState<FormState>(emptyForm);
  const [dirty, setDirty] = useState(false);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!query.data || dirty) return;
    const values = query.data.values;
    setForm({
      publicUrl: values.publicBaseUrl ?? "",
      webUrl: values.webAppUrl ?? "",
      oidcIssuer: values.oidcIssuer ?? "",
      oidcClientId: values.oidcClientId ?? "",
      oidcClientSecret: "",
      telegramApiId: values.telegramApiId ?? "",
      telegramApiHash: "",
      telegramSession: "",
      telegramAdminIds: values.telegramAdminUserIds ?? "",
      fcmServiceAccountJson: ""
    });
  }, [dirty, query.data]);

  const updateField = (field: keyof FormState, value: string) => {
    setDirty(true);
    setForm((current) => ({ ...current, [field]: value }));
  };

  const save = useMutation({
    mutationFn: () => client.updateSettings(toPayload(form)),
    onSuccess: async () => {
      setDirty(false);
      setForm((current) => ({ ...current, oidcClientSecret: "", telegramApiHash: "", telegramSession: "", fcmServiceAccountJson: "" }));
      await queryClient.invalidateQueries({ queryKey: ["settings"] });
    }
  });

  if (query.isLoading) return <SettingsState loading title="설정을 불러오는 중" />;
  if (query.error || !query.data) return <SettingsState error title="설정을 불러오지 못했습니다" body={messageOf(query.error)} action={<button className="button secondary" onClick={() => void query.refetch()}>다시 시도</button>} />;

  const configured = query.data.secretsConfigured;
  return (
    <>
      <header className="page-header settings-page-header">
        <div>
          <p className="eyebrow">시스템 관리</p>
          <h1>설정</h1>
          <p>공개 주소, 인증, Telegram 수집과 Android 푸시 연결을 관리합니다.</p>
        </div>
        <div className="page-actions">
          <button className="button primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            <Save aria-hidden="true" />{save.isPending ? "저장 중…" : "변경 저장"}
          </button>
        </div>
      </header>

      <section className="settings-security-warning" role="note">
        <ShieldAlert aria-hidden="true" />
        <div><strong>비밀정보를 안전하게 취급하세요</strong><p>저장된 secret과 session은 다시 표시되지 않습니다. 새 값을 입력하면 기존 값이 교체되고 관련 연결이 잠시 중단될 수 있습니다.</p></div>
      </section>

      <div className="settings-layout">
        <nav className="settings-index" aria-label="설정 섹션">
          <a href="#settings-address"><Link2 />서비스 주소</a>
          <a href="#settings-database"><Database />데이터베이스</a>
          <a href="#settings-oidc"><LockKeyhole />OIDC 로그인</a>
          <a href="#settings-telegram"><Send />Telegram 소스</a>
          <a href="#settings-fcm"><Cloud />Android 푸시</a>
        </nav>

        <div className="settings-content">
          <SettingsSection id="settings-address" icon={<Link2 />} title="서비스 주소" description="웹훅, 로그인 callback과 Android App Link 생성에 사용하는 외부 주소입니다.">
            <div className="settings-two-column">
              <SettingField label="공개 API URL" hint="외부에서 접근 가능한 HTTPS 기준 주소">
                <input type="url" inputMode="url" value={form.publicUrl} placeholder="https://api.example.com" onChange={(event) => updateField("publicUrl", event.target.value)} />
              </SettingField>
              <SettingField label="관리자 Web URL" hint="브라우저와 Android WebView가 여는 HTTPS 주소">
                <input type="url" inputMode="url" value={form.webUrl} placeholder="https://admin.example.com" onChange={(event) => updateField("webUrl", event.target.value)} />
              </SettingField>
            </div>
          </SettingsSection>

          <SettingsSection id="settings-database" icon={<Database />} title="데이터베이스" description="설치 시 선택한 데이터베이스입니다. 변경하려면 백업·복원 절차를 사용해야 합니다.">
            <div className="database-readonly">
              <Database aria-hidden="true" />
              <div><span>현재 클라이언트</span><strong>{databaseName(query.data.databaseClient)}</strong></div>
              <span className="readonly-badge">읽기 전용</span>
            </div>
          </SettingsSection>

          <SettingsSection id="settings-oidc" icon={<LockKeyhole />} title="OIDC 로그인" description="auth.devpjt.com 같은 OpenID Connect 공급자를 연결합니다." action={<IntegrationTest kind="OIDC" disabled={dirty} />}>
            <div className="settings-two-column">
              <SettingField label="Issuer URL" hint="Discovery 문서의 issuer와 정확히 같아야 합니다.">
                <input type="url" inputMode="url" value={form.oidcIssuer} placeholder="https://auth.devpjt.com" onChange={(event) => updateField("oidcIssuer", event.target.value)} />
              </SettingField>
              <SettingField label="Client ID"><input value={form.oidcClientId} autoCapitalize="none" spellCheck={false} onChange={(event) => updateField("oidcClientId", event.target.value)} /></SettingField>
            </div>
            <SecretField label="Client secret" configured={configured.oidcClientSecret ?? false} field="oidcClientSecret" value={form.oidcClientSecret} visible={showSecrets.oidcClientSecret ?? false} onVisibility={() => setShowSecrets((v) => ({ ...v, oidcClientSecret: !v.oidcClientSecret }))} onChange={(value) => updateField("oidcClientSecret", value)} />
          </SettingsSection>

          <SettingsSection id="settings-telegram" icon={<Send />} title="Telegram 소스 클라이언트" description="외부 채널과 봇을 구독하는 Telegram 사용자 클라이언트 설정입니다." action={<IntegrationTest kind="TELEGRAM_SOURCE" disabled={dirty} />}>
            <div className="integration-current-state"><span className={query.data.sourceClientConnected ? "dot online" : "dot"} /><strong>{query.data.sourceClientConnected ? "소스 클라이언트 연결됨" : "소스 클라이언트 미연결"}</strong></div>
            <div className="settings-two-column">
              <SettingField label="Telegram API ID" hint="my.telegram.org에서 발급한 숫자 ID"><input inputMode="numeric" value={form.telegramApiId} onChange={(event) => updateField("telegramApiId", event.target.value.replace(/[^0-9]/g, ""))} /></SettingField>
              <SecretField compact label="Telegram API hash" configured={configured.telegramApiHash ?? false} field="telegramApiHash" value={form.telegramApiHash} visible={showSecrets.telegramApiHash ?? false} onVisibility={() => setShowSecrets((v) => ({ ...v, telegramApiHash: !v.telegramApiHash }))} onChange={(value) => updateField("telegramApiHash", value)} />
            </div>
            <SecretField label="Telegram session" configured={configured.telegramUserSession ?? false} field="telegramSession" value={form.telegramSession} visible={showSecrets.telegramSession ?? false} multiline onVisibility={() => setShowSecrets((v) => ({ ...v, telegramSession: !v.telegramSession }))} onChange={(value) => updateField("telegramSession", value)} />
            <TelegramAuthSetup apiId={form.telegramApiId} apiHash={form.telegramApiHash} apiHashConfigured={configured.telegramApiHash ?? false} />
            <SettingField label="허용된 관리자 Telegram ID" hint="숫자 ID를 쉼표로 구분합니다. 사용자명은 사용할 수 없습니다."><input inputMode="numeric" value={form.telegramAdminIds} placeholder="123456789, 987654321" onChange={(event) => updateField("telegramAdminIds", event.target.value)} /></SettingField>
          </SettingsSection>

          <SettingsSection id="settings-fcm" icon={<Cloud />} title="Android 푸시 알림" description="Firebase Cloud Messaging service account를 사용해 관리자 앱에 안전한 알림을 보냅니다." action={<IntegrationTest kind="FCM" disabled={dirty} />}>
            <SecretField label="FCM service-account JSON" configured={configured.fcmServiceAccountJson ?? false} field="fcmServiceAccountJson" value={form.fcmServiceAccountJson} visible={showSecrets.fcmServiceAccountJson ?? false} multiline onVisibility={() => setShowSecrets((v) => ({ ...v, fcmServiceAccountJson: !v.fcmServiceAccountJson }))} onChange={(value) => updateField("fcmServiceAccountJson", value)} />
            <p className="settings-footnote"><KeyRound aria-hidden="true" />푸시 본문에는 원문이나 인명을 넣지 않으며, 앱이 인증 후 해당 검토 항목을 다시 조회합니다.</p>
          </SettingsSection>

          {save.error && <p className="settings-error" role="alert"><CircleAlert />{messageOf(save.error)}</p>}
          {save.isSuccess && !dirty && <p className="settings-success" role="status"><CheckCircle2 />설정을 저장했습니다.</p>}
          <div className="settings-mobile-save">
            <button className="button primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}><Save />{save.isPending ? "저장 중…" : "변경 저장"}</button>
          </div>
        </div>
      </div>
    </>
  );
}

function SettingsSection({ id, icon, title, description, action, children }: React.PropsWithChildren<{ id: string; icon: React.ReactNode; title: string; description: string; action?: React.ReactNode }>) {
  return <section className="settings-section" id={id}><header><span className="settings-section-icon">{icon}</span><div><h2>{title}</h2><p>{description}</p></div>{action}</header><div className="settings-section-body">{children}</div></section>;
}

function SettingField({ label, hint, children }: React.PropsWithChildren<{ label: string; hint?: string }>) {
  return <label className="setting-field"><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>;
}

function SecretField({ label, field, configured, value, visible, multiline, compact, onVisibility, onChange }: { label: string; field: string; configured?: boolean; value: string; visible?: boolean; multiline?: boolean; compact?: boolean; onVisibility: () => void; onChange: (value: string) => void }) {
  const placeholder = configured ? "설정됨 · 새 값 입력 시 교체" : "설정되지 않음";
  const input = multiline
    ? <textarea rows={visible ? 6 : 3} value={value} className={!visible ? "secret-masked" : ""} autoComplete="off" spellCheck={false} placeholder={placeholder} aria-label={`${label} 값`} onChange={(event) => onChange(event.target.value)} />
    : <input type={visible ? "text" : "password"} value={value} autoComplete="new-password" spellCheck={false} placeholder={placeholder} aria-label={`${label} 값`} onChange={(event) => onChange(event.target.value)} />;
  return <label className={`setting-field secret-field ${compact ? "compact" : ""}`}><span>{label}{configured && <em><CheckCircle2 />설정됨</em>}</span><div>{input}<button type="button" className="secret-toggle" onClick={onVisibility} aria-label={visible ? `${label} 숨기기` : `${label} 표시`}>{visible ? <EyeOff /> : <Eye />}</button></div><small>저장된 값은 표시하지 않습니다. 비워두면 현재 값을 유지합니다.</small><input type="hidden" name={`${field}-configured`} value={configured ? "true" : "false"} /></label>;
}

function IntegrationTest({ kind, disabled }: { kind: IntegrationKind; disabled: boolean }) {
  const [result, setResult] = useState<IntegrationTestResult | null>(null);
  const test = useMutation({ mutationFn: () => client.testIntegration(kind), onSuccess: setResult });
  return <div className="integration-test"><button className="button secondary" disabled={disabled || test.isPending} title={disabled ? "변경사항을 먼저 저장하세요" : undefined} onClick={() => test.mutate()}><Radio className={test.isPending ? "is-spinning" : ""} />{test.isPending ? "테스트 중" : disabled ? "저장 후 테스트" : "연결 테스트"}</button>{(result || test.error) && <span className={result?.ok ? "test-ok" : "test-failed"} role="status">{result?.ok ? <CheckCircle2 /> : <CircleAlert />}{result?.message ?? result?.identity ?? messageOf(test.error)}</span>}</div>;
}

function TelegramAuthSetup({ apiId, apiHash, apiHashConfigured }: { apiId: string; apiHash: string; apiHashConfigured: boolean }) {
  const queryClient = useQueryClient();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [flow, setFlow] = useState<Awaited<ReturnType<typeof client.startTelegramUserAuth>> | null>(null);
  const start = useMutation({ mutationFn: () => client.startTelegramUserAuth(apiId, apiHash, phoneNumber), onSuccess: setFlow });
  const submit = useMutation({
    mutationFn: () => client.submitTelegramUserAuth(flow!.flowId, flow!.status === "PASSWORD_REQUIRED" ? { password } : { code }),
    onSuccess: async (result) => { setFlow(result); if (result.status === "AUTHORIZED") await queryClient.invalidateQueries({ queryKey: ["settings"] }); }
  });
  const needsCode = flow?.status === "CODE_REQUIRED";
  const needsPassword = flow?.status === "PASSWORD_REQUIRED";
  return <div className="telegram-auth-setup"><div><strong>전화번호로 안전하게 세션 만들기</strong><p>Telegram이 보낸 인증 코드와 2단계 비밀번호는 저장하지 않으며, 완료된 세션만 암호화합니다.</p></div>{!flow || ["FAILED", "AUTHORIZED"].includes(flow.status) ? <div className="settings-two-column"><SettingField label="운영 Telegram 전화번호" hint="국가번호를 포함한 국제 형식"><input inputMode="tel" autoComplete="tel" placeholder="+821012345678" value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} /></SettingField><button className="button secondary" disabled={!apiId || (!apiHash && !apiHashConfigured) || !/^\+[1-9]\d{7,14}$/.test(phoneNumber.replace(/[\s()-]/g, "")) || start.isPending} onClick={() => start.mutate()}>{start.isPending ? "코드 요청 중…" : flow?.status === "AUTHORIZED" ? "다른 계정 연결" : "인증 코드 요청"}</button></div> : <div className="settings-two-column"><SettingField label={needsPassword ? "Telegram 2단계 비밀번호" : "Telegram 인증 코드"}><input type={needsPassword ? "password" : "text"} inputMode={needsPassword ? "text" : "numeric"} autoComplete="one-time-code" value={needsPassword ? password : code} onChange={(event) => needsPassword ? setPassword(event.target.value) : setCode(event.target.value)} /></SettingField><button className="button primary" disabled={(!needsCode && !needsPassword) || submit.isPending || (needsPassword ? !password : !code.trim())} onClick={() => submit.mutate()}>{submit.isPending || flow.status === "VERIFYING" ? "확인 중…" : "인증 완료"}</button></div>}{(start.error || submit.error || flow?.error) && <p className="settings-error"><CircleAlert />{flow?.error ?? messageOf(start.error ?? submit.error)}</p>}{flow?.status === "AUTHORIZED" && <p className="settings-success"><CheckCircle2 />Telegram 사용자 세션을 암호화해 저장하고 수집기를 연결했습니다.</p>}</div>;
}

function SettingsState({ title, body, action, loading, error }: { title: string; body?: string; action?: React.ReactNode; loading?: boolean; error?: boolean }) {
  return <div className="settings-state">{loading ? <span className="spinner" /> : error ? <CircleAlert /> : <ActivityIcon />}<h2>{title}</h2>{body && <p>{body}</p>}{action}</div>;
}

function ActivityIcon() { return <Cloud />; }

function toPayload(form: FormState): Record<string, string> {
  const values: Record<string, string> = {
    publicBaseUrl: form.publicUrl.trim(), webAppUrl: form.webUrl.trim(), oidcIssuer: form.oidcIssuer.trim(), oidcClientId: form.oidcClientId.trim(),
    telegramApiId: form.telegramApiId.trim(), telegramAdminUserIds: form.telegramAdminIds.split(",").map((id) => id.trim()).filter(Boolean).join(",")
  };
  if (form.oidcClientSecret) values.oidcClientSecret = form.oidcClientSecret;
  if (form.telegramApiHash) values.telegramApiHash = form.telegramApiHash;
  if (form.telegramSession) values.telegramUserSession = form.telegramSession;
  if (form.fcmServiceAccountJson) values.fcmServiceAccountJson = form.fcmServiceAccountJson;
  return values;
}

function databaseName(value: string): string {
  const labels: Record<string, string> = { pg: "PostgreSQL", postgres: "PostgreSQL", postgresql: "PostgreSQL", mysql: "MySQL", sqlite: "SQLite" };
  return labels[value.toLowerCase()] ?? value;
}

function messageOf(error: unknown): string { return error instanceof RequestError || error instanceof Error ? error.message : "알 수 없는 오류입니다."; }
